import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchAllUnits,
  fetchUnitWithDetails,
  fetchAreasForUnits,
  createUnit,
  updateUnit,
  deleteUnit,
  getUnitCountByArea,
} from '@/lib/api/units'
import { broadcastInvalidation } from '@/lib/sync'
import type { UnitFilters, UnitFormData } from '@/types/units'
import { toast } from 'sonner'

/**
 * Query key factory for units
 * Provides consistent keys for cache management
 */
export const unitKeys = {
  all: ['units'] as const,
  lists: () => [...unitKeys.all, 'list'] as const,
  list: (filters?: UnitFilters) => [...unitKeys.lists(), filters] as const,
  details: () => [...unitKeys.all, 'detail'] as const,
  detail: (id: string) => [...unitKeys.details(), id] as const,
  buildings: () => [...unitKeys.all, 'buildings'] as const,
  counts: () => [...unitKeys.all, 'counts'] as const,
  count: (buildingId: string) => [...unitKeys.counts(), buildingId] as const,
}

/**
 * Fetch all units across all buildings with optional filtering
 */
export function useUnits(filters?: UnitFilters) {
  return useQuery({
    queryKey: unitKeys.list(filters),
    queryFn: () => fetchAllUnits(filters),
  })
}

/**
 * Fetch a single unit by ID with full details
 */
export function useUnit(id: string | undefined) {
  return useQuery({
    queryKey: unitKeys.detail(id || ''),
    queryFn: () => fetchUnitWithDetails(id!),
    enabled: !!id,
  })
}

/**
 * Fetch buildings available for unit creation (T-hangar types only)
 */
export function useBuildingsForUnits() {
  return useQuery({
    queryKey: unitKeys.buildings(),
    queryFn: fetchAreasForUnits,
  })
}

/**
 * Fetch unit count summary for a building
 */
export function useUnitCount(buildingId: string | undefined) {
  return useQuery({
    queryKey: unitKeys.count(buildingId || ''),
    queryFn: () => getUnitCountByArea(buildingId!),
    enabled: !!buildingId,
  })
}

/**
 * Create a new unit
 */
export function useCreateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: Omit<UnitFormData, 'sequence'> & { sequence?: number }) =>
      createUnit(data),
    onSuccess: () => {
      const keys = [unitKeys.lists(), unitKeys.counts()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Unit created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create unit', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing unit
 */
export function useUpdateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UnitFormData> }) =>
      updateUnit(id, data),
    onSuccess: (_, { id }) => {
      const keys = [unitKeys.lists(), unitKeys.detail(id), unitKeys.counts()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Unit updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update unit', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a unit (soft delete)
 */
export function useDeleteUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteUnit(id),
    onSuccess: () => {
      const keys = [unitKeys.lists(), unitKeys.counts()] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Unit deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete unit', {
        description: error.message,
      })
    },
  })
}
